/**
 * Das Spielbrett als Geometrie: Korpus, Rahmen, Strasse, Feldmarkierungen,
 * Hotelgelaende mit aufgedruckten Grundrissen, Bank und Rathaus.
 *
 * Jede Flaeche liegt auf ihrer eigenen Hoehe aus `EBENE`. Es gibt keine zwei
 * deckungsgleichen Flaechen und damit kein Z-Fighting.
 */
import * as THREE from 'three'
import { HOTELS, HOTEL_IDS } from '../core/data/hotels'
import {
  BRETT,
  FELDER,
  STRASSE,
  GELAENDE,
  SPIELFLAECHE,
  amtsGebaeude,
  feldTrenner,
  gelaendeSchild,
  linienStrecke,
  stadtparkRand,
  strassenBand,
  versetzteKurve,
} from '../core/data/spielplan'
import type { AussenBereich } from '../core/data/spielplan'
import type { Grundriss, Polygon, Punkt } from '../core/geometrie'
import { abstandZumRand, grundrissEcken, huelle, imPolygon } from '../core/geometrie'
import { EBENE } from './ebenen'
import { BAUSTILE } from './baustile'
import { deckband, quader, verbinde, walmdach } from './formen'
import {
  asphalt,
  dachhaut,
  fassade,
  feldsymbol,
  grund,
  pflaster,
  rasen,
  schriftzug,
} from './texturen'

const KORPUS_HOEHE = 14
const RAHMEN_HOEHE = 7

/**
 * Ein Polygon der Spielflaeche als aufgestellter Koerper.
 *
 * Achtung, hier steckt eine Falle: `rotateX(-PI/2)` bildet einen Formpunkt (u, v)
 * auf (u, 0, -v) ab, spiegelt also die z-Achse. Wer die Form mit (x, z) fuellt,
 * bekommt ein an der x-Achse gespiegeltes Brett - und merkt es erst, wenn die
 * Hotels auf den falschen Feldern liegen. Deshalb wird hier mit -z gefuellt.
 */
function extrudiertesPolygon(
  punkte: Polygon,
  von: number,
  bis: number,
): THREE.BufferGeometry {
  const shape = new THREE.Shape(punkte.map((p) => new THREE.Vector2(p.x, -p.z)))
  const g = new THREE.ExtrudeGeometry(shape, { depth: bis - von, bevelEnabled: false })
  // Nach der Drehung liegt die Extrusion bei y von 0 bis Tiefe. Verschoben wird
  // deshalb um `von`, nicht um `bis` - sonst schwebt die Platte um ihre eigene
  // Dicke zu hoch und verdeckt alles, was darauf gehoert.
  g.rotateX(-Math.PI / 2)
  g.translate(0, von, 0)
  return g
}

/** Ein flaches Rechteck aus einem Grundriss, auf Hoehe `y`. */
export function rissFlaeche(riss: Grundriss, y: number, schrumpf = 1): THREE.BufferGeometry {
  const g = new THREE.PlaneGeometry(riss.breite * schrumpf, riss.tiefe * schrumpf)
  g.rotateX(-Math.PI / 2)
  g.rotateY(-riss.drehung)
  g.translate(riss.mitte.x, y, riss.mitte.z)
  return g
}

function schild(
  text: string,
  riss: Grundriss,
  y: number,
  hoehe: number,
  farbe: string,
): THREE.Mesh {
  const t = schriftzug(text, farbe)
  const breite = hoehe * 4
  const g = new THREE.PlaneGeometry(breite, hoehe)
  g.rotateX(-Math.PI / 2)
  // Auf einem echten Brett sitzen Spieler ringsherum; auf einem Bildschirm nicht.
  // Deshalb wird eine Beschriftung, die kopfueber stuende, um 180 Grad gedreht.
  const kopfueber = Math.cos(riss.drehung) < 0
  g.rotateY(-riss.drehung + (kopfueber ? Math.PI : 0))
  g.translate(riss.mitte.x, y, riss.mitte.z)
  return new THREE.Mesh(
    g,
    new THREE.MeshBasicMaterial({ map: t, transparent: true, depthWrite: false }),
  )
}

/** Der komplette, unveraenderliche Teil des Bretts. */
export function baueBrett(): THREE.Group {
  const gruppe = new THREE.Group()
  gruppe.name = 'brett'

  // Korpus: dunkle Kanten, helle Spielflaeche.
  const korpus = new THREE.Mesh(
    new THREE.BoxGeometry(BRETT.breite, KORPUS_HOEHE, BRETT.tiefe),
    [
      new THREE.MeshStandardMaterial({ color: '#242830', roughness: 0.85 }),
      new THREE.MeshStandardMaterial({ color: '#242830', roughness: 0.85 }),
      new THREE.MeshStandardMaterial({ color: '#dcd3c1', roughness: 0.95 }),
      new THREE.MeshStandardMaterial({ color: '#1b1e24', roughness: 0.9 }),
      new THREE.MeshStandardMaterial({ color: '#242830', roughness: 0.85 }),
      new THREE.MeshStandardMaterial({ color: '#242830', roughness: 0.85 }),
    ],
  )
  korpus.position.y = -KORPUS_HOEHE / 2
  korpus.receiveShadow = true
  gruppe.add(korpus)

  // Rahmen als vier Leisten, damit die Spielflaeche eine echte Kante bekommt.
  const rahmenMaterial = new THREE.MeshStandardMaterial({ color: '#8f3324', roughness: 0.55 })
  const leisten: THREE.BufferGeometry[] = []
  const halbB = BRETT.breite / 2
  const halbT = BRETT.tiefe / 2
  const r = BRETT.rahmen
  for (const [bx, bz, px, pz] of [
    [BRETT.breite, r, 0, -halbT + r / 2],
    [BRETT.breite, r, 0, halbT - r / 2],
    [r, BRETT.tiefe - 2 * r, -halbB + r / 2, 0],
    [r, BRETT.tiefe - 2 * r, halbB - r / 2, 0],
  ] as const) {
    const g = quader(bx, bz, 0, RAHMEN_HOEHE)
    g.translate(px, 0, pz)
    leisten.push(g)
  }
  const rahmen = new THREE.Mesh(verbinde(leisten), rahmenMaterial)
  rahmen.castShadow = true
  rahmen.receiveShadow = true
  gruppe.add(rahmen)

  // Schmale helle Einlage an der Innenkante des Rahmens.
  const einlagen: THREE.BufferGeometry[] = []
  const e = 2.4
  for (const [bx, bz, px, pz] of [
    [BRETT.breite - 2 * r, e, 0, -halbT + r + e / 2],
    [BRETT.breite - 2 * r, e, 0, halbT - r - e / 2],
    [e, BRETT.tiefe - 2 * r - 2 * e, -halbB + r + e / 2, 0],
    [e, BRETT.tiefe - 2 * r - 2 * e, halbB - r - e / 2, 0],
  ] as const) {
    const g = quader(bx, bz, 0, 0.4)
    g.translate(px, 0, pz)
    einlagen.push(g)
  }
  gruppe.add(
    new THREE.Mesh(
      verbinde(einlagen),
      new THREE.MeshStandardMaterial({ color: '#e8cf94', roughness: 0.5, metalness: 0.25 }),
    ),
  )

  // Hotelgelaende.
  for (const id of Object.keys(GELAENDE) as AussenBereich[]) {
    const gel = GELAENDE[id]
    const istHotel = HOTEL_IDS.includes(id as never)
    const farbe = istHotel ? HOTELS[id as keyof typeof HOTELS].farbe : '#cfd4dc'
    const flaeche = new THREE.Mesh(
      extrudiertesPolygon(gel.polygon, 0, EBENE.gelaende),
      new THREE.MeshStandardMaterial({
        color: farbe,
        map: kacheln(grund(), 6, 6),
        roughness: 0.95,
      }),
    )
    flaeche.receiveShadow = true
    gruppe.add(flaeche)

    if (istHotel) {
      const hotel = id as keyof typeof HOTELS
      // Aufgedruckte Grundrisse: hier gehoert ein Gebaeude hin.
      const risse = [...gel.bauplaetze, gel.freizeitpark]
      const platten = risse.map((riss) => rissFlaeche(riss, EBENE.grundriss))
      const abdruck = new THREE.Mesh(
        verbinde(platten),
        new THREE.MeshStandardMaterial({
          color: '#000000',
          transparent: true,
          opacity: 0.26,
          roughness: 1,
        }),
      )
      gruppe.add(abdruck)

      const innen = verbinde(risse.map((riss) => rissFlaeche(riss, EBENE.grundrissInnen, 0.86)))
      gruppe.add(
        new THREE.Mesh(
          innen,
          new THREE.MeshStandardMaterial({ color: farbe, roughness: 1 }),
        ),
      )

      gruppe.add(
        schild(
          HOTELS[hotel].name.toUpperCase(),
          gelaendeSchild(hotel),
          EBENE.schrift,
          26,
          '#ffffff',
        ),
      )

      const gruen = zierbaeume(gel.polygon, [...risse, gelaendeSchild(hotel)])
      if (gruen) gruppe.add(gruen)
    }
  }

  // Titel auf der unteren Rahmenleiste.
  gruppe.add(
    schild(
      'FÜNF STERNE',
      { mitte: { x: 0, z: BRETT.tiefe / 2 - r / 2 }, breite: 1, tiefe: 1, drehung: 0 },
      RAHMEN_HOEHE + 0.05,
      15,
      '#f4dfae',
    ),
  )

  // Stadtpark in der Brettmitte: die Flaeche, die kein Hotel bebauen darf.
  const park = stadtparkRand()
  const parkFlaeche = new THREE.Mesh(
    extrudiertesPolygon(park, 0, EBENE.gelaende - 0.7),
    new THREE.MeshStandardMaterial({ map: kacheln(rasen('#57a066', '#3f7a4e'), 5, 5), roughness: 1 }),
  )
  parkFlaeche.receiveShadow = true
  gruppe.add(parkFlaeche)

  const weg = new THREE.Mesh(
    scheibe(96, 106, EBENE.gelaende - 0.5),
    new THREE.MeshStandardMaterial({
      map: kacheln(pflaster(), 30, 1),
      color: '#d9cfb8',
      roughness: 0.95,
    }),
  )
  gruppe.add(weg)

  const teich = new THREE.Mesh(
    scheibe(0, 62, EBENE.gelaende - 0.3, 0.58),
    new THREE.MeshStandardMaterial({ color: '#3f8fc4', roughness: 0.16, metalness: 0.12 }),
  )
  gruppe.add(teich)
  // Baumgruppen im Stadtpark.
  const parkbaeume: THREE.BufferGeometry[] = []
  const parkstaemme: THREE.BufferGeometry[] = []
  for (let i = 0; i < 14; i++) {
    const w = (i / 14) * Math.PI * 2 + 0.3
    const rx = 128 + (i % 3) * 9
    const rz = 76 + ((i * 7) % 3) * 7
    const x = Math.cos(w) * rx
    const z = Math.sin(w) * rz
    const gr = 5.5 + ((i * 5) % 3) * 1.4
    const stamm = quader(gr * 0.4, gr * 0.4, EBENE.gelaende - 0.7, EBENE.gelaende + gr * 1.1)
    stamm.translate(x, 0, z)
    parkstaemme.push(stamm)
    const krone = kugelkrone(gr, EBENE.gelaende + gr * 1.1, gr * 1.8)
    krone.translate(x, 0, z)
    parkbaeume.push(krone)
  }
  const kronenMesh = new THREE.Mesh(
    verbinde(parkbaeume),
    new THREE.MeshStandardMaterial({ color: '#2f7a45', roughness: 0.95 }),
  )
  const staemmeMesh = new THREE.Mesh(
    verbinde(parkstaemme),
    new THREE.MeshStandardMaterial({ color: '#6b4a2e', roughness: 1 }),
  )
  for (const m of [kronenMesh, staemmeMesh]) {
    m.castShadow = true
    gruppe.add(m)
  }

  gruppe.add(
    schild(
      'STADTPARK',
      { mitte: { x: 0, z: 74 }, breite: 100, tiefe: 22, drehung: 0 },
      EBENE.gelaende - 0.1,
      20,
      '#f3f7ee',
    ),
  )

  // Strasse.
  const { innen, aussen } = strassenBand()
  const fahrbahn = new THREE.Mesh(
    deckband(innen, aussen, EBENE.strasse),
    new THREE.MeshStandardMaterial({ map: asphalt(), roughness: 0.95 }),
  )
  fahrbahn.receiveShadow = true
  gruppe.add(fahrbahn)

  // Gehwege: ein heller Streifen beidseits der Fahrbahn, damit die Strasse eine
  // Kante bekommt und die Gebaeude nicht direkt am Asphalt stehen.
  const gehwegMaterial = new THREE.MeshStandardMaterial({
    map: kacheln(pflaster(), 40, 3),
    roughness: 0.9,
  })
  for (const seite of [1, -1]) {
    const nah = versetzteKurve(seite * (STRASSE.breite / 2))
    const fern = versetzteKurve(seite * (STRASSE.breite / 2 + 9))
    const g = seite === 1 ? deckband(fern, nah, EBENE.gehweg) : deckband(nah, fern, EBENE.gehweg)
    const mesh = new THREE.Mesh(g, gehwegMaterial)
    mesh.receiveShadow = true
    gruppe.add(mesh)
  }

  // Trennstriche zwischen den Feldern.
  const striche: THREE.BufferGeometry[] = []
  for (const [a, b] of feldTrenner()) {
    striche.push(strecke(a, b, 1.4, EBENE.trenner))
  }
  gruppe.add(
    new THREE.Mesh(
      verbinde(striche),
      new THREE.MeshBasicMaterial({ color: '#d8443a' }),
    ),
  )

  // Bank- und Rathauslinie in Gelb.
  const linien: THREE.BufferGeometry[] = []
  for (const welches of ['bank', 'rathaus'] as const) {
    const [a, b] = linienStrecke(welches)
    linien.push(strecke(a, b, 4.5, EBENE.linie))
  }
  gruppe.add(
    new THREE.Mesh(verbinde(linien), new THREE.MeshBasicMaterial({ color: '#f2c230' })),
  )

  // Feldsymbole.
  for (const feld of FELDER) {
    const groesse = 17
    const g = new THREE.PlaneGeometry(groesse, groesse)
    g.rotateX(-Math.PI / 2)
    g.rotateY(-Math.atan2(feld.richtung.z, feld.richtung.x))
    g.translate(feld.mitte.x, EBENE.symbol, feld.mitte.z)
    gruppe.add(
      new THREE.Mesh(
        g,
        new THREE.MeshBasicMaterial({
          map: feldsymbol(feld.art),
          transparent: true,
          depthWrite: false,
        }),
      ),
    )
  }

  // Bank und Rathaus als eigene Gebaeude.
  for (const [welches, beschriftung, wand, dach] of [
    ['bank', 'BANK', '#e6e1d5', '#4a5568'],
    ['rathaus', 'RATHAUS', '#e0d6c4', '#7d5a3c'],
  ] as const) {
    const riss = amtsGebaeude(welches)
    gruppe.add(amtsbau(riss, wand, dach))
    // Die Beschriftung steht neben dem Gebaeude, nicht dahinter: der zweite
    // Grundriss des Sektors ist frei.
    gruppe.add(
      schild(beschriftung, GELAENDE[welches].freizeitpark, EBENE.schrift, 22, '#ffffff'),
    )
  }

  return gruppe
}

/**
 * Zierbaeume auf einer freien Flaeche.
 *
 * Gesucht werden Rasterpunkte, die im Polygon liegen, genug Abstand zum Rand haben
 * und keinen Bauplatz beruehren. Damit fuellt die Begruenung die Restflaeche eines
 * Gelaendes, ohne je einem Gebaeude in die Quere zu kommen - und ohne dass irgendwo
 * eine Position von Hand gepflegt werden muesste.
 */
function zierbaeume(
  polygon: Polygon,
  belegt: readonly Grundriss[],
  abstand = 34,
): THREE.Group | null {
  const k = huelle(polygon)
  const kronen: THREE.BufferGeometry[] = []
  const staemme: THREE.BufferGeometry[] = []
  let n = 0
  for (let x = k.minX; x <= k.maxX; x += abstand) {
    for (let z = k.minZ; z <= k.maxZ; z += abstand) {
      const punkt = { x, z }
      if (!imPolygon(punkt, polygon)) continue
      if (abstandZumRand(punkt, polygon) < 16) continue
      const zuNah = belegt.some((riss) => {
        const dx = punkt.x - riss.mitte.x
        const dz = punkt.z - riss.mitte.z
        return Math.hypot(dx, dz) < Math.max(riss.breite, riss.tiefe) * 0.75 + 12
      })
      if (zuNah) continue
      n++
      if (n % 2 !== 0) continue
      // Bewusst niedrig: hohe Baeume verdecken aus flachem Blickwinkel die
      // Gelaendebeschriftung und die aufgedruckten Grundrisse.
      const gr = 4.2 + (n % 3) * 0.9
      const stamm = quader(gr * 0.38, gr * 0.38, EBENE.gelaende - 0.7, EBENE.gelaende + gr)
      stamm.translate(x, 0, z)
      staemme.push(stamm)
      const krone = kugelkrone(gr, EBENE.gelaende + gr, gr * 1.7)
      krone.translate(x, 0, z)
      kronen.push(krone)
    }
  }
  if (kronen.length === 0) return null
  const gruppe = new THREE.Group()
  const a = new THREE.Mesh(
    verbinde(kronen),
    new THREE.MeshStandardMaterial({ color: '#33794a', roughness: 0.95 }),
  )
  const b = new THREE.Mesh(
    verbinde(staemme),
    new THREE.MeshStandardMaterial({ color: '#6b4a2e', roughness: 1 }),
  )
  for (const m of [a, b]) {
    m.castShadow = true
    gruppe.add(m)
  }
  return gruppe
}

/** Baumkrone: gestauchte Halbkugel. */
function kugelkrone(radius: number, von: number, hoehe: number): THREE.BufferGeometry {
  const g = new THREE.SphereGeometry(radius, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2)
  g.scale(1, hoehe / radius, 1)
  g.translate(0, von, 0)
  return g
}

/** Kreisring oder Scheibe, flach auf Hoehe `y`, optional gestaucht. */
function scheibe(innen: number, aussen: number, y: number, stauchung = 1): THREE.BufferGeometry {
  const g = new THREE.RingGeometry(innen, aussen, 72)
  g.rotateX(-Math.PI / 2)
  g.scale(1, 1, stauchung)
  g.translate(0, y, 0)
  return g
}

function strecke(a: Punkt, b: Punkt, breite: number, y: number): THREE.BufferGeometry {
  const dx = b.x - a.x
  const dz = b.z - a.z
  const l = Math.hypot(dx, dz)
  const g = new THREE.PlaneGeometry(l, breite)
  g.rotateX(-Math.PI / 2)
  g.rotateY(-Math.atan2(dz, dx))
  g.translate((a.x + b.x) / 2, y, (a.z + b.z) / 2)
  return g
}

function amtsbau(riss: Grundriss, wand: string, dachfarbe: string): THREE.Group {
  const gruppe = new THREE.Group()
  const b = riss.breite
  const t = riss.tiefe
  const H = 34
  const koerper = new THREE.Mesh(
    quader(b, t, -0.6, H * 0.7),
    new THREE.MeshStandardMaterial({
      map: kacheln(fassade(wand, '#3c4a5e', 8, 3), 2, 1),
      roughness: 0.85,
    }),
  )
  const dach = new THREE.Mesh(
    walmdach(b, t, H * 0.7, H * 0.3, 0.6),
    new THREE.MeshStandardMaterial({
      map: kacheln(dachhaut(dachfarbe, '#00000033', 10), 2, 2),
      roughness: 0.8,
    }),
  )
  for (const m of [koerper, dach]) {
    m.castShadow = true
    m.receiveShadow = true
    gruppe.add(m)
  }
  gruppe.position.set(riss.mitte.x, EBENE.gelaende, riss.mitte.z)
  gruppe.rotation.y = -riss.drehung
  return gruppe
}

/** Eine Textur mit eigener Wiederholung, ohne das Original zu veraendern. */
export function kacheln(t: THREE.Texture, x: number, y: number): THREE.Texture {
  const k = t.clone()
  k.needsUpdate = true
  k.wrapS = THREE.RepeatWrapping
  k.wrapT = THREE.RepeatWrapping
  k.repeat.set(x, y)
  return k
}

/** Die Grundflaeche des Freizeitparks eines Hotels, als flache Rasenplatte. */
export function parkBoden(hotel: keyof typeof BAUSTILE): THREE.Mesh {
  const riss = GELAENDE[hotel].freizeitpark
  const [hell, dunkel] = BAUSTILE[hotel].park
  return new THREE.Mesh(
    rissFlaeche(riss, EBENE.grundriss + 0.02),
    new THREE.MeshStandardMaterial({ map: kacheln(rasen(hell, dunkel), 2, 2), roughness: 1 }),
  )
}

export { grundrissEcken, SPIELFLAECHE }
